import { z } from "zod";

export const ProgressionSchema = z.strictObject({
  levelCap: z.int().min(1),
  xpFormula: z.strictObject({ base: z.number().positive(), exponent: z.number().positive() }),
  statPointsPerLevel: z.int().min(0),
  /** Fraction of the current level's required XP lost on death. */
  deathXpPenalty: z.number().min(0).max(1),
  deathPenaltyMinLevel: z.int().min(1),
  /** Mob XP scaling by level difference (player minus mob). */
  mobXp: z.strictObject({
    penaltyStartsAtLevelsBelow: z.int().min(0),
    penaltyPerLevel: z.number().min(0),
    minMult: z.number().min(0).max(1),
    bonusPerLevelAbove: z.number().min(0),
    maxBonus: z.number().min(0),
  }),
});
export type Progression = z.infer<typeof ProgressionSchema>;

export const XpTableSchema = z.array(z.strictObject({ level: z.int().min(1), xp: z.int().min(0) }));
export type XpTable = z.infer<typeof XpTableSchema>;

export const MovementSchema = z.strictObject({
  /** Meters per second at base. */
  baseSpeed: z.number().positive(),
  /** Allowed overshoot factor for input-rate checks (PRD: max speed x 1.1). */
  speedTolerance: z.number().min(1),
  /** Each client move input represents exactly this much simulated time. */
  inputStepMs: z.int().positive(),
  maxStrikes: z.int().positive(),
  strikeWindowMs: z.int().positive(),
});
export type Movement = z.infer<typeof MovementSchema>;

/** One step of the weapon and armor ladder: an Upgrade Scroll, gold and a roll. */
export const UpgradeStepSchema = z.strictObject({
  from: z.int().min(0),
  /** false: this step takes gold only, no Upgrade Scroll (the first, cheap steps). */
  needsScroll: z.boolean().optional(),
  successPct: z.number().min(0).max(100),
  onFail: z.enum(["none", "stay", "destroy"]),
  gold: z.int().min(0),
  statGainPct: z.number().min(0),
});

export const UpgradeTableSchema = z
  .strictObject({
    maxPlus: z.int().min(1),
    steps: z.array(UpgradeStepSchema),
  })
  .refine((t) => t.steps.length === t.maxPlus, "steps must cover 0..maxPlus-1")
  .refine((t) => t.steps.every((s, i) => s.from === i), "steps must be ordered by from");
export type UpgradeTable = z.infer<typeof UpgradeTableSchema>;

/** One step of the jewelry ladder: no roll and no risk, `copies` identical pieces merge. */
export const JewelryStepSchema = z.strictObject({
  from: z.int().min(0),
  gold: z.int().min(0),
  statGainPct: z.number().min(0),
});

export const JewelryTableSchema = z
  .strictObject({
    maxPlus: z.int().min(1),
    /** Identical pieces (same item, same +) merged into one piece one level higher. */
    copies: z.int().min(2),
    steps: z.array(JewelryStepSchema),
  })
  .refine((t) => t.steps.length === t.maxPlus, "steps must cover 0..maxPlus-1")
  .refine((t) => t.steps.every((s, i) => s.from === i), "steps must be ordered by from");
export type JewelryTable = z.infer<typeof JewelryTableSchema>;

/**
 * Two ladders: weapons and armor climb to +10 with an Upgrade Scroll and a roll; jewelry and
 * cloaks climb to +5 by merging identical pieces with a Jewelry Scroll (never destroyed).
 * Glows are store cosmetics and never upgrade.
 */
export const UpgradeTablesSchema = z.strictObject({
  gear: UpgradeTableSchema,
  jewelry: JewelryTableSchema,
  /** A Fortune Stone multiplies the step's success chance by this (capped at 100%). */
  fortuneMult: z.number().min(1),
});
export type UpgradeTables = z.infer<typeof UpgradeTablesSchema>;

export const TerrainSchema = z.strictObject({
  seed: z.int(),
  /** Peak height in meters. */
  amplitude: z.number().min(0),
  /** Feature size in meters (larger = smoother hills). */
  scale: z.number().positive(),
  /** Radius around the spawn point that stays flat (town). */
  flatRadius: z.number().min(0),
  /** Hills grow to amplitude * farBoost at the zone edge (gentle near town, rugged far out). */
  farBoost: z.number().min(0).optional(),
  /** Distance from the center (square metric) where the border mountains start rising. */
  edgeStart: z.number().positive().optional(),
  /** Height of the border mountains at the zone edge. */
  edgeHeight: z.number().min(0).optional(),
});
export type Terrain = z.infer<typeof TerrainSchema>;

/** "HH:MM", 24 h clock. */
const ClockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const MobSpawnSchema = z.strictObject({
  mob: z.string(),
  x: z.number(),
  z: z.number(),
  count: z.int().positive(),
  radius: z.number().min(0),
  /**
   * Scheduled spawn (world bosses): the group appears only at these local times (the zone's
   * `schedule.timezone`) and does not respawn on a timer. A kill waits for the next time.
   */
  times: z.array(ClockTime).min(1).optional(),
  /** Where the scheduled spawn happens, for the announcement ("in the Bowl"). */
  place: z.string().optional(),
});

export const StructureSchema = z.strictObject({
  id: z.string().regex(/^[a-z0-9_]+$/),
  /** Footprint in meters (local x = width, local z = depth, door faces local +z). */
  w: z.number().positive(),
  d: z.number().positive(),
  /** Height in meters (camera occlusion). */
  h: z.number().positive(),
  collider: z.enum(["box", "circle", "none"]),
});
export const StructuresSchema = z.array(StructureSchema);
export type StructureDef = z.infer<typeof StructureSchema>;

const PointTuple = z.tuple([z.number(), z.number()]);

export const TownSchema = z.strictObject({
  name: z.string(),
  /** Banner colors: ashen (red, neutral), verdant (blue, human), grim (black and red, orc). */
  palette: z.enum(["ashen", "verdant", "grim"]),
  /** Stone wall with gatehouses, or a wooden stake palisade. */
  wall: z.enum(["stone", "stakes", "ruin"]).optional(),
  /** Trees, bushes and flowers per 100 m2 on the free ground inside the wall. */
  greenery: z.number().min(0).optional(),
  plaza: z.strictObject({ x: z.number(), z: z.number(), radius: z.number().positive() }),
  palisade: z.strictObject({
    radius: z.number().positive(),
    /** Degrees, 0 = +z (north), 90 = +x (east). */
    gateAngles: z.array(z.number()),
    gateWidth: z.number().positive(),
    postSpacing: z.number().positive(),
  }),
  paths: z.array(
    z.strictObject({ width: z.number().positive(), points: z.array(PointTuple).min(2) }),
  ),
  structures: z.array(
    z.strictObject({
      type: z.string(),
      x: z.number(),
      z: z.number(),
      /** Degrees around Y; 0 = door faces +z. */
      rot: z.number(),
      /** Flies this nation's banner (a keep in a war zone's human fort), not the town's. */
      nation: z.enum(["human", "orc"]).optional(),
    }),
  ),
});
export type Town = z.infer<typeof TownSchema>;

const Circle = z.strictObject({ x: z.number(), z: z.number(), r: z.number().positive() });

export const BIOMES = [
  "forest",
  "pine_forest",
  "birch_grove",
  "meadow",
  "farmland",
  "marsh",
  "rocky",
  "graveyard",
  "ashland",
  "glacier",
  "lava",
] as const;
export type Biome = (typeof BIOMES)[number];

/** Nature and water outside the town: scattered deterministically from `seed`. */
export const WorldSchema = z.strictObject({
  seed: z.int(),
  water: z.strictObject({
    /** Water surface height; river beds and lakes are carved below it. */
    level: z.number(),
    /** Depth of the carved bed below the surface (shallow: walkable). */
    depth: z.number().positive(),
    rivers: z.array(
      z.strictObject({
        width: z.number().positive(),
        points: z.array(PointTuple).min(2),
        /** Too deep to wade: crossed only on a bridge (town structure "bridge"). */
        deep: z.boolean().optional(),
      }),
    ),
    lakes: z.array(Circle),
  }),
  /** Areas with their own mix of plants and rocks; later entries win where they overlap. */
  regions: z.array(
    z.strictObject({
      /** Shown on the map and when you walk in ("Wolfpine Forest"). */
      name: z.string().optional(),
      biome: z.enum(BIOMES),
      x: z.number(),
      z: z.number(),
      r: z.number().positive(),
      /** Objects per 100 m2. */
      density: z.number().min(0),
    }),
  ),
  /** Grass tufts and flowers per 100 m2 everywhere that is not water, road or town. */
  groundCover: z.number().min(0),
  /** Kept free of scatter (mob camps, landmarks). */
  clearings: z.array(Circle),
});
export type World = z.infer<typeof WorldSchema>;

export const ZoneSchema = z.strictObject({
  id: z.string().regex(/^[a-z0-9_]+$/),
  name: z.string(),
  /** Square zone edge length in meters, centered on origin. */
  size: z.number().positive(),
  spawn: z.strictObject({ x: z.number(), z: z.number() }),
  spawnRadius: z.number().min(0),
  playerCap: z.int().positive(),
  terrain: TerrainSchema,
  mobSpawns: z.array(MobSpawnSchema),
  /**
   * Mini-bosses that turn up now and then somewhere in the zone (never the same spot): one
   * at a time, near monsters of about their level, gone again if nobody kills it in time.
   */
  roamingBosses: z
    .strictObject({
      mobs: z.array(z.string()).min(1),
      /** Wait before the next one appears (random within), after a kill or a departure. */
      everyMs: z.tuple([z.int().positive(), z.int().positive()]),
      /** It moves on (disappears) after this long unless killed. */
      lifetimeMs: z.int().positive(),
      /** It appears near a monster camp within this many levels of its own. */
      levelWindow: z.int().min(0),
    })
    .optional(),
  town: TownSchema.optional(),
  /** Where stalls may open in a zone without a town (the ko-assets zones, local experiment). */
  market: z
    .strictObject({ x: z.number(), z: z.number(), radius: z.number().positive() })
    .optional(),
  world: WorldSchema.optional(),
  /** Gloomy light and sky: "grim" darker, redder, foggier (orc lands); "void" the Rift's violet dusk. */
  mood: z.enum(["bright", "grim", "void"]).optional(),
  /** Players can attack each other here. */
  pvp: z.boolean().optional(),
  /**
   * Per-nation arrival and respawn points (the PvP colony). Players inside safeRadius of
   * their own base cannot be attacked.
   */
  bases: z
    .strictObject({
      human: z.strictObject({ x: z.number(), z: z.number() }),
      orc: z.strictObject({ x: z.number(), z: z.number() }),
      safeRadius: z.number().min(0),
      /**
       * Fire towers around each base: every `everyMs` a player of the other nation within
       * `radius` of the base takes a fireball of `dmgPct` of max HP from the nearest tower.
       */
      guard: z
        .strictObject({
          radius: z.number().positive(),
          everyMs: z.int().positive(),
          dmgPct: z.number().positive().max(100),
          /**
           * Set: every tower within this many meters shoots (each `dmgPct`); unset: only the
           * nearest one does.
           */
          rangeM: z.number().positive().optional(),
          towers: z.strictObject({
            human: z.array(z.strictObject({ x: z.number(), z: z.number() })).min(1),
            orc: z.array(z.strictObject({ x: z.number(), z: z.number() })).min(1),
          }),
        })
        .optional(),
    })
    .optional(),
  /** Timezone and warnings for scheduled spawns (mobSpawns with `times`). */
  schedule: z
    .strictObject({
      timezone: z.string().refine((tz) => {
        try {
          new Intl.DateTimeFormat("en-US", { timeZone: tz });
          return true;
        } catch {
          return false;
        }
      }, "unknown timezone"),
      /** Server-wide warnings this many minutes before a scheduled spawn. */
      warnMinutes: z.array(z.int().positive()),
    })
    .optional(),
  /**
   * An arena: level floor out to floorRadius, banks rising `depth` meters to the rim at
   * `radius`, then easing back to the natural ground over the same distance.
   */
  bowl: z
    .strictObject({
      x: z.number(),
      z: z.number(),
      floorRadius: z.number().positive(),
      radius: z.number().positive(),
      depth: z.number().positive(),
    })
    .optional(),
  /** Homeland of this race (the nation gate sends its players here). */
  nation: z.enum(["human", "orc"]).optional(),
  /**
   * Local experiment (branch ko-assets-local): the ground, objects and collision come from a
   * converted ko-assets map (art-src/vendor/ko/zones/<id>), not from terrain/town/world.
   */
  ko: z.strictObject({ map: z.string() }).optional(),
  /** Local experiment: new characters begin here instead of the first town (Moradon). */
  start: z.boolean().optional(),
  /**
   * Travel gates: click one to pick a destination from every zone you may enter (see
   * travelDestinations). rot in degrees; the arch faces local +z.
   */
  portals: z
    .array(
      z.strictObject({
        x: z.number(),
        z: z.number(),
        rot: z.number(),
        label: z.string(),
        /** A gate to this one zone only (the Rift gate); ordinary gates list every zone. */
        only: z.string().optional(),
        /**
         * Local experiment: where a ko warp gate's building stands, when its spot (x, z) had
         * to move off its walls; the click area goes there.
         */
        pick: z.strictObject({ x: z.number(), z: z.number() }).optional(),
        /**
         * Local experiment: a gate on a KO map with no warp stone of its own (Eslant): drawn as
         * the game's own gate instead of a click area on the map's object.
         */
        drawn: z.literal(true).optional(),
        /**
         * Local experiment: a ko warp gate's own list (the server map's _WARP_INFO): each place
         * by name, in a zone at a spot, for a fee in gold. Shown instead of the zone list.
         */
        warps: z
          .array(
            z.strictObject({
              id: z.int(),
              name: z.string(),
              zone: z.string(),
              x: z.number(),
              z: z.number(),
              pay: z.int().min(0),
            }),
          )
          .optional(),
      }),
    )
    .optional(),
  /**
   * A zone open only at set times (the Umbral Rift): its gate in `from` opens at each of
   * `opens` for `openMinutes`, and each player may stay `stayMinutes` from first entering,
   * then is sent back to `from`. It never shows in the ordinary travel list.
   */
  timed: z
    .strictObject({
      from: z.string(),
      timezone: z.string(),
      opens: z.array(z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)).min(1),
      openMinutes: z.int().positive(),
      stayMinutes: z.int().positive(),
    })
    .optional(),
  /**
   * A war zone (Morgard): open twice a week for `minutes` from `start` on each of `days`
   * (0 = Sunday, local to `timezone`). Every travel gate lists it (faded while closed). Each
   * nation's War Keeper spawns at `keepers` when it opens; the nation that slays the other's
   * keeper wins, and its players get `bonusPct` more XP, gold and drops until the next war
   * begins. Everyone goes to `returnTo` when the war ends, `endAfterWinMs` after a win. The
   * zones in `closes` shut for the war (the Colony: everyone should be at war).
   */
  war: z
    .strictObject({
      days: z.array(z.int().min(0).max(6)).min(1),
      start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
      timezone: z.string().refine((tz) => {
        try {
          new Intl.DateTimeFormat("en-US", { timeZone: tz });
          return true;
        } catch {
          return false;
        }
      }, "unknown timezone"),
      minutes: z.int().positive(),
      warnMinutes: z.array(z.int().positive()),
      keepers: z.strictObject({
        human: z.strictObject({ mob: z.string(), x: z.number(), z: z.number() }),
        orc: z.strictObject({ mob: z.string(), x: z.number(), z: z.number() }),
      }),
      returnTo: z.string(),
      /** Zones closed while the war is on: no gate goes there and their players go to returnTo. */
      closes: z.array(z.string()),
      endAfterWinMs: z.int().min(0),
      /** A keeper that fell without deciding the war (a GM's blow) is back after this long. */
      keeperReturnMs: z.int().min(0),
      bonusPct: z.strictObject({
        xp: z.number().min(0).max(100),
        gold: z.number().min(0).max(100),
        drop: z.number().min(0).max(100),
      }),
    })
    .optional(),
  /**
   * The Crystal Clash arena: an event twice a day at each of `starts` (local to `timezone`).
   * Sign-up opens `signupMinutes` before; at the start everyone signed up is brought here, one
   * arena per level bracket (the zone is copied per bracket, `bracket` set on each copy). The
   * nation whose player strikes the last blow on the crystal at `crystal` wins, or the first to
   * `killsToWin` player kills, or after `minutes` the one with more kills (a draw with as many).
   * Winners get the bracket's `win` coffer, the other side `lose`, broken on the Riftstone Reel.
   * `exitsPerDay` leaves a match early without a penalty; one more keeps the account out of
   * the event for `penaltyHours`.
   */
  clash: z
    .strictObject({
      /** Local experiment: the event's name ("Border Defence War"; the Crystal Clash's by default). */
      name: z.string().optional(),
      /** Local experiment: what stands in the middle ("altar"; "crystal" by default). */
      objective: z.string().optional(),
      timezone: z.string().refine((tz) => {
        try {
          new Intl.DateTimeFormat("en-US", { timeZone: tz });
          return true;
        } catch {
          return false;
        }
      }, "unknown timezone"),
      starts: z.array(ClockTime).min(1),
      signupMinutes: z.int().positive(),
      minutes: z.int().positive(),
      killsToWin: z.int().positive(),
      /** A bracket with fewer signed up on either side is called off. */
      minPerSide: z.int().positive(),
      /** Sign-ups per nation and bracket. */
      maxPerSide: z.int().positive(),
      crystal: z.strictObject({ x: z.number(), z: z.number() }),
      /** Everyone goes back this long after the match is decided. */
      endAfterWinMs: z.int().min(0),
      exitsPerDay: z.int().min(0),
      penaltyHours: z.int().positive(),
      /** Where a player goes when their way back is not known. */
      returnTo: z.string(),
      /**
       * Game masters may sign up too (the beta: to watch the event from inside). They count for
       * their side, cannot strike players or the crystal, earn no coffer and are never barred.
       */
      gmsMayJoin: z.boolean().optional(),
      brackets: z
        .array(
          z.strictObject({
            minLevel: z.int().min(1),
            maxLevel: z.int().min(1),
            /** The crystal monster (mobs.json, `crystal: true`). */
            crystal: z.string(),
            win: z.string(),
            lose: z.string(),
            /** A second prize beside the first (KO's Treasure Chests: the winners' a grade up). */
            winExtra: z.string().optional(),
            loseExtra: z.string().optional(),
          }),
        )
        .min(1),
      /** Which bracket this copy of the arena is for (set when the zones load). */
      bracket: z.int().min(0).optional(),
    })
    .optional(),
  /** Characters below this level cannot travel here through a gate. */
  minLevel: z.int().min(1).optional(),
  /**
   * The monsters here never start a fight, whatever their own reach: they answer a blow only
   * (KO's Moradon, owner 2026-10-31: the first levels' town ground).
   */
  calmMobs: z.literal(true).optional(),
  /**
   * Local experiment: how much more the monsters here pay than the homelands' (KO's Colony
   * Zone and Eslant, owner 2026-10-27): their loot gold and every item roll's chance.
   */
  pay: z.strictObject({ gold: z.number().min(1), drop: z.number().min(1) }).optional(),
  /**
   * Local experiment: KO's Bifrost. Twice a day (`starts`, in `timezone`) the monument (`mob`,
   * spawned in its `zone` at `x`, `z`) can be struck for `assaultMinutes`; the nation whose
   * player breaks it may enter this zone for `openMinutes`, then everyone goes to `returnTo`.
   */
  bifrost: z
    .strictObject({
      starts: z.array(z.string().regex(/^\d{2}:\d{2}$/)).min(1),
      timezone: z.string(),
      warnMinutes: z.array(z.int().positive()),
      assaultMinutes: z.int().positive(),
      openMinutes: z.int().positive(),
      monument: z.strictObject({
        zone: z.string(),
        mob: z.string(),
        x: z.number(),
        z: z.number(),
      }),
      returnTo: z.string(),
    })
    .optional(),
});
export const ZonesSchema = z.array(ZoneSchema).min(1);
export type Zone = z.infer<typeof ZoneSchema>;

/** The in-game clock: one day every dayLengthMs of real time, the same for everyone. */
export const ClockSchema = z.strictObject({
  dayLengthMs: z.int().positive(),
  /** Game hour at the Unix epoch, so the day does not start at midnight. */
  hourOffset: z.number().min(0).max(24),
});
export type Clock = z.infer<typeof ClockSchema>;

export const CombatSchema = z.strictObject({
  /**
   * Local experiment (branch ko-assets-local): ko's own formulas for stats and hits (deriveStats,
   * rollDamage), with each class's ko coefficients (ClassSchema `ko`).
   */
  ko: z.boolean().optional(),
  /** Local experiment: how far a bow's plain shot reaches, in meters (KO's arrows never run out). */
  koBowRange: z.number().positive().optional(),
  /**
   * Local experiment: how long a fallen player lies where they fell before the return to town
   * (KO's maps: time for a priest's resurrection; the death window's Return town goes at once).
   * `playerRespawnMs` when left out.
   */
  koDeadMs: z.int().positive().optional(),
  tickHz: z.int().positive(),
  /**
   * The red (HP) and blue (MP) potions every character carries without end (keys 9 and 0):
   * they restore base + perLevel x level, once per cooldown each. Nobody buys or loots them.
   */
  /**
   * How levels grow a character and a monster: share = ((level - from) / (cap - from)) ^ power,
   * 0 up to `from` (the starter levels stay as they are), never above 1. Characters add share x their class's levelHp/levelMp/levelAp; monsters get
   * 1 + share x mobBonus times their HP and attack power; potions 1 + share x potionBonus.
   */
  levelGrowth: z.strictObject({
    from: z.int().min(1),
    cap: z.int().positive(),
    power: z.number().positive(),
    mobBonus: z.number().min(0),
    potionBonus: z.number().min(0),
  }),
  potions: z.strictObject({
    cooldownMs: z.int().positive(),
    hpBase: z.number().min(0),
    hpPerLevel: z.number().min(0),
    /**
     * The blue potion restores this percent of the class's usual MP pool at the level
     * (`typicalMaxMp`), once per `mpCooldownMs`: it covers part of a fight's skills, never all.
     */
    mpPoolPct: z.number().positive(),
    mpCooldownMs: z.int().positive(),
  }),
  /**
   * KO's potions (local experiment): from each listed level the tier's item and what it restores,
   * KO's Waters (HP) and Potions (MP).
   */
  koPotions: z
    .strictObject({
      hp: z.array(z.tuple([z.int().min(1), z.int().positive(), z.string()])),
      mp: z.array(z.tuple([z.int().min(1), z.int().positive(), z.string()])),
    })
    .optional(),
  /** Party buffs and party heals reach members this close to the caster. */
  partyBuffRangeM: z.number().positive(),
  /** Minimum time between two boss abilities (and after a boss is pulled). */
  bossAbilityGapMs: z.int().min(0),
  gcdMs: z.int().min(0),
  rangeToleranceM: z.number().min(0),
  hitChanceBase: z.number().min(0).max(1),
  hitChancePenaltyPerLevel: z.number().min(0),
  hitChanceMin: z.number().min(0).max(1),
  /** Damage = raw x raw / (raw + DEF x defFactor), raw = AP x skill multiplier (combat.ts). */
  defFactor: z.number().min(0),
  damageRandMin: z.number().positive(),
  damageRandMax: z.number().positive(),
  critMult: z.number().min(1),
  critPerDex: z.number().min(0),
  critCap: z.number().min(0).max(1),
  pvpDamageMult: z.number().min(0),
  /** A plain attack's share of attack power (R): below every damaging skill. */
  basicAttackMult: z.number().positive(),
  /** Healing a player receives from skills while in PvP (see pvpHealWindowMs). */
  pvpHealMult: z.number().min(0),
  /** A player is in PvP this long after dealing damage to a player or taking it from one. */
  pvpHealWindowMs: z.int().min(0),
  hpBase: z.number().min(1),
  hpPerVit: z.number().min(0),
  mpBase: z.number().min(0),
  mpPerInt: z.number().min(0),
  apPerPrimary: z.number().min(0),
  outOfCombatAfterMs: z.int().min(0),
  /** Most damage reduction a player can stack against one class (special jewelry). */
  classResistCapPct: z.number().min(0).max(100),
  /** Most an element's resistance can take off that element's weapon damage. */
  elementResistCapPct: z.number().min(0).max(100),
  regen: z.strictObject({
    hpPctPerSecInCombat: z.number().min(0),
    hpPctPerSecOutOfCombat: z.number().min(0),
    mpPctPerSecInCombat: z.number().min(0),
    mpPctPerSecOutOfCombat: z.number().min(0),
  }),
  playerRespawnMs: z.int().min(0),
  /**
   * A common monster comes back sooner the more players hunt near its camp: its respawn time
   * less perExtraPlayerPct for each player past the first within crowdRangeM, never below
   * minPct of it (bosses and scheduled monsters keep their own times).
   */
  mobRespawn: z.strictObject({
    crowdRangeM: z.number().positive(),
    perExtraPlayerPct: z.number().min(0),
    minPct: z.number().positive().max(100),
  }),
  /** Return-to-town channel; any damage taken during it cancels the trip. */
  townRecallMs: z.int().min(0),
});
export type Combat = z.infer<typeof CombatSchema>;

export const StatKeySchema = z.enum(["str", "dex", "int", "vit"]);
export type StatKey = z.infer<typeof StatKeySchema>;
export const ClassIdSchema = z.enum(["warrior", "rogue", "mage", "priest"]);
/** Race is also the nation (PvP side). */
export const RaceSchema = z.enum(["human", "orc"]);
export type Race = z.infer<typeof RaceSchema>;
export const GenderSchema = z.enum(["male", "female"]);
export type Gender = z.infer<typeof GenderSchema>;
export type ClassId = z.infer<typeof ClassIdSchema>;

export const ClassSchema = z.strictObject({
  /** Local experiment: the class's ko COEFFICIENT row (HP, MP, SP, AC and its weapon's hit). */
  ko: z
    .strictObject({
      hp: z.number(),
      mp: z.number(),
      sp: z.number(),
      ac: z.number(),
      hit: z.number(),
    })
    .optional(),
  id: ClassIdSchema,
  name: z.string(),
  primary: StatKeySchema,
  hpBonusPct: z.number(),
  /**
   * What levels add on top of stats and gear, reached at the level cap and growing with
   * `combat.levelGrowth` (slowly at first): max HP, max MP and attack power.
   */
  levelHp: z.number().min(0),
  levelMp: z.number().min(0),
  levelAp: z.number().min(0),
  /**
   * Skills cost their `mpCost` times the class's usual MP pool at the caster's level over this
   * (`skillMpCost`): costs grow with the pool, so MP runs out at every level if skills are
   * used without pause, and MP gear makes them last longer.
   */
  mpCostRef: z.number().positive(),
  baseStats: z.strictObject({ str: z.int(), dex: z.int(), int: z.int(), vit: z.int() }),
  weapon: z.strictObject({
    name: z.string(),
    apBase: z.number().min(0),
    attackIntervalMs: z.int().positive(),
    range: z.number().positive(),
  }),
  /** Given on character creation: equipped (weapon) or put in the bag. */
  starterItems: z.array(
    z.strictObject({
      item: z.string(),
      qty: z.int().positive().optional(),
      equip: z.enum(["weapon", "offhand", "head", "chest", "hands", "feet", "neck"]).optional(),
    }),
  ),
});
export const ClassesSchema = z.array(ClassSchema).length(ClassIdSchema.options.length);
export type ClassDef = z.infer<typeof ClassSchema>;

export const StatusKindSchema = z.enum(["stun", "root", "freeze", "slow"]);
export type StatusKind = z.infer<typeof StatusKindSchema>;
/** `xp` and `gold` (percents) raise what kills and loot pay, like Premium (the Novice scrolls). */
export const BuffStatSchema = z.enum([
  "def",
  "ap",
  "maxHp",
  "damageTaken",
  "moveSpeed",
  "xp",
  "gold",
  /** Local experiment: KO's magic attack power (a mage's Absolute Power), in percent. */
  "magic",
  /** Local experiment: attacks this much faster (KO's Slow curse: -30), in percent. */
  "attackSpeed",
  /**
   * Local experiment: KO's resistance buffs (flat points beside the items', under the same cap)
   * and its hit and evasion points (Nimble Wind, a rogue's Illusion on an enemy).
   */
  "resFire",
  "resIce",
  "resLightning",
  "resPoison",
  "resShadow",
  "resMagic",
  "hit",
  "evade",
  /** Local experiment: the Genie's time (KO's automatic hunt, ko/koGenie.ts); changes no stat. */
  "genie",
]);
export type BuffStat = z.infer<typeof BuffStatSchema>;

export const SkillAreaSchema = z.discriminatedUnion("shape", [
  z.strictObject({
    shape: z.literal("cone"),
    radius: z.number().positive(),
    angleDeg: z.number().positive().max(360),
    maxTargets: z.int().positive(),
  }),
  z.strictObject({
    shape: z.literal("circle_self"),
    radius: z.number().positive(),
    maxTargets: z.int().positive().optional(),
  }),
  z.strictObject({
    shape: z.literal("circle_point"),
    radius: z.number().positive(),
    maxTargets: z.int().positive().optional(),
  }),
]);
export type SkillArea = z.infer<typeof SkillAreaSchema>;

export const SkillEffectSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("damage"),
    mult: z.number().positive(),
    hits: z.int().positive().optional(),
    /** Multiplier used instead of `mult` when the caster is stealthed. */
    stealthMult: z.number().positive().optional(),
    area: SkillAreaSchema.optional(),
  }),
  /** Poison on the target, or on every enemy the skill's damage hit. */
  z.strictObject({
    type: z.literal("dot"),
    mult: z.number().positive(),
    durationMs: z.int().positive(),
    tickMs: z.int().positive(),
  }),
  /** Heals the target, or you and your party members within `radius` of you. */
  z.strictObject({
    type: z.literal("heal"),
    mult: z.number().positive(),
    radius: z.number().positive().optional(),
  }),
  /** Heal over time on the target. `mult` is the total over the duration. */
  z.strictObject({
    type: z.literal("hot"),
    mult: z.number().positive(),
    durationMs: z.int().positive(),
    tickMs: z.int().positive(),
  }),
  /** Applied to every entity hit by the skill's damage effect, or to the enemy target if it has none. */
  z.strictObject({
    type: z.literal("status"),
    status: StatusKindSchema,
    durationMs: z.int().positive(),
    pct: z.number().min(0).max(100).optional(),
    /** Percent chance, rolled for each enemy hit; always lands when left out. */
    chance: z.number().positive().max(100).optional(),
    /** Only lands on players (a PvP control), never on monsters. */
    playersOnly: z.boolean().optional(),
  }),
  z.strictObject({
    type: z.literal("buff"),
    /** `target`: the friendly target (a KO buff cast on another player). */
    scope: z.enum(["self", "party", "target"]),
    stat: BuffStatSchema,
    pct: z.number(),
    /** Added to the stat before the percents (KO's AC +50, max HP +200). */
    flat: z.number().optional(),
    durationMs: z.int().positive(),
  }),
  /**
   * Weakens enemies: every entity hit by the skill's damage effect, or the enemy target if
   * it has none. pct is signed like a buff (def -25 = 25% less defense, damageTaken +20 =
   * 20% more damage taken).
   */
  z.strictObject({
    type: z.literal("debuff"),
    stat: BuffStatSchema,
    pct: z.number(),
    durationMs: z.int().positive(),
    /**
     * Local experiment: an area curse with no hit of its own (KO's Torment): every enemy within
     * this many meters of the aimed point (or the target, or the caster) takes it.
     */
    radius: z.number().positive().optional(),
    /** Local experiment: taken off the stat before the percents (an Illusion's -20 hit). */
    flat: z.number().optional(),
  }),
  z.strictObject({ type: z.literal("taunt"), radius: z.number().positive() }),
  z.strictObject({ type: z.literal("dash") }),
  /**
   * Clears the target's (or the caster's) debuffs: poisons, slows, roots, freezes, hexes.
   * With `radius`: you and every party member within it.
   */
  z.strictObject({ type: z.literal("cleanse"), radius: z.number().positive().optional() }),
  /** Brings every party member in the zone who is out of combat to the caster. */
  z.strictObject({ type: z.literal("summon"), radius: z.number().positive() }),
  /** Instant teleport forward along facing; breaks roots and slows. */
  z.strictObject({ type: z.literal("blink"), distance: z.number().positive() }),
  /**
   * Local experiment: KO's MAGIC_TYPE3 as its servers run it. `magic` hits the target (or
   * everyone within `radius` of it) for `amount` before KO's charisma and resistance math, an
   * `attribute` of 1 fire, 2 ice, 3 lightning, 4 magic, 5 curse, 6 poison; `heal` gives
   * `amount` HP flat; `dot` adds `amount` more over `durationMs` (damage or healing).
   */
  z.strictObject({
    type: z.literal("ko"),
    kind: z.enum(["magic", "heal"]),
    amount: z.number().min(0),
    attribute: z.int().min(0).max(9).optional(),
    radius: z.number().positive().optional(),
    dot: z
      .strictObject({ amount: z.number().positive(), durationMs: z.int().positive() })
      .optional(),
  }),
  z.strictObject({
    type: z.literal("stealth"),
    durationMs: z.int().positive(),
    moveSpeedPct: z.number(),
  }),
  /**
   * Local experiment: KO's resurrections. Raises a fallen player of the caster's side where
   * they lie (the target, or the nearest within the skill's range) and gives back `xpPct` of
   * the XP the death took.
   */
  z.strictObject({ type: z.literal("revive"), xpPct: z.number().min(0).max(100) }),
  /**
   * Local experiment: KO's drains. `hpPct` of the enemy target's current HP taken and given to
   * the caster (Blood Drain, Vampiric Touch; at most `hpPct` x 6% of the caster's own HP), `mp`
   * of its MP gone (Clear Mana, Sweep Mana).
   */
  z.strictObject({
    type: z.literal("drain"),
    hpPct: z.number().positive().optional(),
    mp: z.number().positive().optional(),
  }),
  /**
   * Local experiment: KO's Gate and Escape (`home`: back to the town the caster would rise in,
   * with `party` every party member in the zone too) and Descent (`ally`: to the target party
   * member's side).
   */
  z.strictObject({
    type: z.literal("teleport"),
    to: z.enum(["home", "ally"]),
    party: z.boolean().optional(),
  }),
  /** Local experiment: KO's Reverse Life, the enemy target's HP blessings (heals over time, max HP) gone. */
  z.strictObject({ type: z.literal("dispel") }),
  /** Local experiment: KO's Sacrifice, `amount` of the caster's HP (never the last) to a party member. */
  z.strictObject({ type: z.literal("sacrifice"), amount: z.number().positive() }),
]);
export type SkillEffect = z.infer<typeof SkillEffectSchema>;

export const SkillSchema = z
  .strictObject({
    id: z.string().regex(/^[a-z0-9_]+$/),
    classId: ClassIdSchema,
    name: z.string(),
    unlockLevel: z.int().min(1),
    cooldownMs: z.int().min(0),
    castMs: z.int().min(0),
    mpCost: z.int().min(0),
    /** Meters. 0 for self / untargeted skills. */
    range: z.number().min(0),
    target: z.enum(["enemy", "ally", "point", "none"]),
    /** At least one, unless the skill is one of KO's passives (`koPassive`). */
    effects: z.array(SkillEffectSchema),
    /**
     * Local experiment: one of KO's master passives, always on once learned: damage taken
     * (`damageTakenPct`, -15 = 15% less), defense while HP is at 30% or below (`lowHpDefPct`),
     * MP recovery while MP is at 30% or below (`lowMpRegenPct`).
     */
    koPassive: z
      .strictObject({
        damageTakenPct: z.number().optional(),
        lowHpDefPct: z.number().optional(),
        lowMpRegenPct: z.number().optional(),
      })
      .optional(),
    /** Local experiment: KO's skill tree (skill_magic_main iNeedSkill: 1055, 1096...). */
    koTree: z.int().optional(),
    /** Local experiment: one of KO's archery skills, shot from a bow (`bow` on the weapon). */
    koBow: z.literal(true).optional(),
    /**
     * Local experiment: KO's effects (fx.tbl ids): on the caster while casting and as it fires
     * (`self`, at its joint `selfJoint`), flying to the target (`fly`), and on the target (`target`,
     * at `targetJoint`, -1 its feet).
     */
    koFx: z
      .strictObject({
        self: z.int().optional(),
        selfJoint: z.int().optional(),
        fly: z.int().optional(),
        target: z.int().optional(),
        targetJoint: z.int().optional(),
      })
      .optional(),
  })
  .refine((s) => s.effects.length > 0 || !!s.koPassive, {
    message: "a skill needs an effect unless it is a KO passive",
  });
export const SkillsSchema = z.array(SkillSchema);
export type SkillDef = z.infer<typeof SkillSchema>;

export const MobSchema = z.strictObject({
  id: z.string().regex(/^[a-z0-9_]+$/),
  name: z.string(),
  level: z.int().min(1),
  hp: z.int().positive(),
  /** XP for a kill at equal level. */
  xp: z.int().min(0),
  ap: z.number().min(0),
  def: z.number().min(0),
  attackIntervalMs: z.int().positive(),
  attackRange: z.number().positive(),
  /** 0 = passive, only fights back. */
  aggroRadius: z.number().min(0),
  leashRadius: z.number().positive(),
  moveSpeed: z.number().positive(),
  /** Body radius in meters, added to range checks against this mob. */
  radius: z.number().positive(),
  respawnMs: z.tuple([z.int().min(0), z.int().min(0)]),
  color: z.string().regex(/^#[0-9a-f]{6}$/i),
  scale: z.number().positive(),
  /**
   * Which body the client builds: "species" or "species:variant" (e.g. "wolf:dire"), or
   * "ko:<look>" for a converted ko-assets model (local experiment).
   */
  model: z.string().optional(),
  /** World boss: longer respawn, bigger name plate. */
  boss: z.boolean().optional(),
  /**
   * Local experiment: one of the land's bosses (KO's Talos, Snake Queen, Eslant's five...):
   * the whole server hears when it rises and when it falls.
   */
  announce: z.boolean().optional(),
  /**
   * A nation's own fighter (the War Keepers of Morgard): it never attacks that nation's
   * players and they cannot attack it.
   */
  nation: z.enum(["human", "orc"]).optional(),
  /**
   * An event monster a GM brings (/eventmob): every player sharing the kill gets `xpPct` percent
   * of the XP their own level needs, whatever their level, instead of the usual level-scaled XP.
   */
  event: z.strictObject({ xpPct: z.number().positive().max(50) }).optional(),
  /** National Points for the killer and party members near (spendable, not the ranking). */
  np: z.int().positive().optional(),
  /**
   * Training dummy: stands still, never fights back, never dies (its health refills), gives
   * no XP and no loot. For practising skills in town.
   */
  dummy: z.boolean().optional(),
  /**
   * A Crystal Clash crystal: stands still and never fights back, either nation may strike it
   * (a GM may not), it never heals, and it gives no XP and no loot.
   */
  crystal: z.boolean().optional(),
  /**
   * Boss attacks. Each one marks the ground for castMs (players can step out), then hits
   * every player in the area for mult x a normal hit.
   * slam: circle around the boss; cleave: 120 degree cone in front; nova: ring between
   * radius / 2 and radius (safe up close); meteor: circle on a random player's spot.
   */
  abilities: z
    .array(
      z.strictObject({
        id: z.string().regex(/^[a-z0-9_]+$/),
        name: z.string(),
        kind: z.enum(["slam", "cleave", "nova", "meteor"]),
        radius: z.number().positive(),
        mult: z.number().positive(),
        castMs: z.int().min(0),
        cooldownMs: z.int().positive(),
        /** Optional stun or slow on everyone hit. */
        status: z
          .strictObject({
            kind: z.enum(["stun", "slow"]),
            durationMs: z.int().positive(),
            pct: z.number().min(0).max(100).optional(),
          })
          .optional(),
      }),
    )
    .optional(),
});
export const MobsSchema = z.array(MobSchema);
export type MobDef = z.infer<typeof MobSchema>;

/**
 * Percent bonuses (glows, wings and store cloaks): "np" to National Points from player kills,
 * "xp" to experience from monsters, "gold" to gold picked up from loot, "drop" to the chance of
 * every item roll on a kill.
 */
export const ItemStatKeySchema = z.enum([
  "ap",
  "def",
  "hp",
  "mp",
  "str",
  "dex",
  "int",
  "vit",
  "np",
  "xp",
  "gold",
  "drop",
  // Special jewelry only: percent less damage taken from players of that class.
  "resWarrior",
  "resRogue",
  "resMage",
  "resPriest",
  // Percent less damage from elemental weapons of that element (capped, see combat.json).
  "resFire",
  "resIce",
  "resLightning",
  "resPoison",
  "resShadow",
  // Local experiment, KO's item lines: MagicR (percent off KO's magic-attribute spells), HP and
  // MP drain (percent of a weapon hit's damage back as HP or MP), hit and evasion (percent
  // points on the hit chance, the attacker's hit against the target's evasion).
  "resMagic",
  "hpDrain",
  "mpDrain",
  "hit",
  "evade",
]);
export type ItemStatKey = z.infer<typeof ItemStatKeySchema>;
export const ItemStatsSchema = z.partialRecord(ItemStatKeySchema, z.number());
export type ItemStats = z.infer<typeof ItemStatsSchema>;

/** PRD equipment slots. `none` = cannot be equipped (potions, materials). */
export const ItemSlotSchema = z.enum([
  "weapon",
  "offhand",
  "head",
  "chest",
  // Local experiment: ko armor's fifth piece (pads), worn under the chest.
  "legs",
  "hands",
  "feet",
  "ring",
  "neck",
  "earring",
  "belt",
  "cape",
  "glow",
  "wings",
  "mantle",
  "reward",
  // KO's Power Up Store costumes (local experiment, owner 2026-10-28): worn like an item
  "costume",
  "none",
]);
export type ItemSlot = z.infer<typeof ItemSlotSchema>;

export const GradeSchema = z.enum(["common", "magic", "rare", "unique"]);
export type Grade = z.infer<typeof GradeSchema>;

export const ItemSchema = z.strictObject({
  id: z.string().regex(/^[a-z0-9_]+$/),
  name: z.string(),
  kind: z.enum(["weapon", "armor", "accessory", "consumable", "material", "scroll"]),
  slot: ItemSlotSchema,
  levelReq: z.int().min(1),
  /** Sell price in gold. */
  value: z.int().min(0),
  /** Only these classes may equip it (weapons, shields). */
  classes: z.array(ClassIdSchema).optional(),
  stats: ItemStatsSchema,
  /** Max stack size for consumables and materials. */
  stack: z.int().min(2).optional(),
  use: z
    .strictObject({
      hp: z.int().min(0).optional(),
      mp: z.int().min(0).optional(),
      cooldownMs: z.int().min(0),
      /** Adds this much Genie time to the account's balance (the Genie Scroll). */
      genieMs: z.int().positive().optional(),
      /**
       * A scroll's effect for `durationMs`: a flat `value` added to the stat (max HP, defense,
       * attack power), or a percent with `pct` (move speed). Kept through death and logout.
       */
      buff: z
        .strictObject({
          stat: BuffStatSchema,
          value: z.number().positive(),
          pct: z.boolean().optional(),
          durationMs: z.int().positive(),
        })
        .optional(),
    })
    .optional(),
  /** Boss jewelry: always drops as a unique with exactly these stats (no random lines). */
  unique: z.boolean().optional(),
  /**
   * Used up to change the character: `race` turns them to the other race and nation (only on
   * neutral ground, out of their clan and party), `gender` to the other gender.
   */
  change: z.enum(["race", "gender"]).optional(),
  /** Never leaves its owner: cannot be thrown away, sold or traded (ranking rewards). */
  bound: z.boolean().optional(),
  /**
   * No longer made anywhere (no drop, shop, store, Reel or reward gives it): kept so a copy
   * someone still holds keeps working, left out of the website's database.
   */
  retired: z.boolean().optional(),
  /** Opens a window while carried (the Market Scroll opens the market). Never used up. */
  opens: z.enum(["market"]).optional(),
  /** A big weapon held in both hands: no shield (offhand) beside it. */
  twoHanded: z.boolean().optional(),
  /** Local experiment: one of KO's bows or crossbows (Item_Org kind 70, 71): it shoots from afar. */
  bow: z.literal(true).optional(),
  /** KO's + ladder: + level ("1".."10") to the stats it adds (Item_Ext, local experiment). */
  plusBonus: z.record(z.string(), ItemStatsSchema).optional(),
  /** The same for a magic and a rare copy (KO's other kinds of the item). */
  plusBonusMagic: z.record(z.string(), ItemStatsSchema).optional(),
  plusBonusRare: z.record(z.string(), ItemStatsSchema).optional(),
  /**
   * Local experiment: KO's name for a magic or rare copy at +1 to +10 (Item_Ext's header of the
   * row that copy's ladder reads: "Strength", "Saint", "Agility"), written before the name.
   */
  koNames: z
    .strictObject({ magic: z.array(z.string()).optional(), rare: z.array(z.string()).optional() })
    .optional(),
  /** A one-handed weapon that may also go in the left hand (KO's daggers and swords, local experiment). */
  dual: z.boolean().optional(),
  /** Glows: which hand glow look (store.json glows) the item shows while worn. */
  glowLook: z.string().optional(),
  /** Wings: which wing look (store.json wings) the item shows on your back while worn. */
  wingLook: z.string().optional(),
  /** Store cloaks: which cloak look (store.json cloaks) the item shows on your back while worn. */
  mantleLook: z.string().optional(),
  /**
   * KO's unique weapon glow (local experiment): the fx bundles (by file name) whose frames burn
   * on the weapon and whose sparks fly round it, from its Effect2 (Item_Org column 25) / 1000
   * in the newer client's fx.tbl: Raptor's purple uniq_poison and uniq_poisonp.
   */
  koGlow: z.tuple([z.string(), z.string()]).optional(),
  /** Costumes: which armor skin (store.json skins) the item shows while worn. */
  costume: z.string().optional(),
  /**
   * What a scroll or stone is for: `gear` upgrades weapons and armor, `jewelry` merges
   * jewelry, `fortune` raises the odds.
   */
  scroll: z.enum(["gear", "jewelry", "fortune", "enchant"]).optional(),
  /** Gear scrolls: multiplies the roll's success chance (the Best Upgrade Scroll). */
  successMult: z.number().min(1).optional(),
  /** Local experiment: KO's Low and Middle Class Upgrade Scrolls take items up to this class. */
  maxClass: z.int().min(1).max(3).optional(),
  /**
   * Local experiment: KO's Enchant and Immune Scrolls (`scroll: "enchant"`) turn a plain piece
   * into its own kind with that stat at the anvil: `kind` (an `ENCHANT_KINDS` key), the highest
   * KO item class it takes (1 low, 2 middle, 3 high), the chance, and a failure breaks the piece.
   */
  enchant: z
    .strictObject({
      kind: z.enum(["str", "hp", "dex", "int", "magic", "immune", "elemental"]),
      maxClass: z.int().min(1).max(3),
      successPct: z.number().min(1).max(100),
    })
    .optional(),
  /**
   * Local experiment: the kinds an Enchant Scroll makes of this piece, each an index into ko.json
   * `enchants` (its + ladder, "0" to "10", from LibreKO's rows of that kind), and its KO item
   * class (1 low, 2 middle, 3 high).
   */
  koEnchant: z.record(z.string(), z.int().min(0)).optional(),
  koClass: z.int().min(1).max(3).optional(),
  /** Local experiment: the kind an enchanted copy (`<id>~<kind>`) was made into. */
  enchanted: z.string().optional(),
  /** Client icon recipe. */
  icon: z.string(),
  /**
   * Gear tier, for the look (colors, plates, trims) and the icon's backdrop: 0 starter, 1 iron,
   * 2 steel, 3 mithril, 4 dragon, 5 the top set. Class armor of one tier shares its level and
   * price; its look is the class's own (outfit.ts).
   */
  tier: z.int().min(0).max(5).optional(),
  /**
   * Elemental weapons: they always burn in their element's color, and every hit deals
   * `elementDmg` extra damage of that element (flat, not raised by + levels). The element must
   * match the weapon's look and name (shadow: dark violet flames, poison: violet bubbles).
   */
  element: z.enum(["fire", "ice", "lightning", "poison", "shadow"]).optional(),
  elementDmg: z.int().positive().optional(),
  /** Local experiment: an Elemental Scroll's copy, its element's damage at +0 to +10. */
  elementByPlus: z.array(z.int().min(0)).optional(),
  /** Local experiment: the converted ko-assets weapon model held in the hand. */
  koModel: z.string().optional(),
});
export const ItemsSchema = z.array(ItemSchema);
export type ItemDef = z.infer<typeof ItemSchema>;

const WeightedPool = z.array(z.tuple([z.string(), z.number().positive()])).min(1);

const GradeWeightsSchema = z.strictObject({
  common: z.number().min(0),
  magic: z.number().min(0),
  rare: z.number().min(0),
});
export type GradeWeights = z.infer<typeof GradeWeightsSchema>;

export const DropsSchema = z.strictObject({
  /** Grade weights for equipment drops (PRD: 70 / 24 / 5.5; unique only from bosses). */
  /**
   * Grade weights of dropped gear by who drops it: common monsters by their level's gear band
   * (economy.json gearBands), bosses their own; the Riftstone Reel rolls as "mid".
   */
  grades: z.strictObject({
    low: GradeWeightsSchema,
    mid: GradeWeightsSchema,
    high: GradeWeightsSchema,
    boss: GradeWeightsSchema,
  }),
  /** The + a dropped weapon or armor piece comes with: [plus, weight]. */
  gearPlus: z.array(z.tuple([z.int().min(0).max(10), z.number().positive()])).min(1),
  /** Loot belongs to the top damager for this long, then anyone may take it. */
  lootOwnMs: z.int().min(0),
  lootLifetimeMs: z.int().positive(),
  mobs: z.record(
    z.string(),
    z.strictObject({
      gold: z.tuple([z.int().min(0), z.int().min(0)]),
      rolls: z.array(
        z.strictObject({
          chance: z.number().min(0).max(1),
          pool: WeightedPool,
          /** Only in these zones (a monster found in several zones can drop differently). */
          zones: z.array(z.string()).optional(),
        }),
      ),
    }),
  ),
  rollLines: z.strictObject({
    magic: z.tuple([z.int().min(0), z.int().min(0)]),
    rare: z.tuple([z.int().min(0), z.int().min(0)]),
    stats: z.array(z.strictObject({ stat: ItemStatKeySchema, min: z.number(), max: z.number() })),
    /** Roll values scale with item level: value * (1 + perLevel * (levelReq - 1)). */
    perLevel: z.number().min(0),
  }),
});
export type Drops = z.infer<typeof DropsSchema>;

/** A login or first week reward item; `days` rents it (a store look that runs out). */
const RewardItemSchema = z.strictObject({
  item: z.string(),
  qty: z.int().positive(),
  days: z.int().positive().optional(),
});

const DailyRewardSchema = z.strictObject({
  days: z
    .array(
      z.strictObject({
        items: z.array(RewardItemSchema).min(1),
        premiumDays: z.int().positive().optional(),
        /** An armor skin worn for `days` (added to what the account has left of it). */
        skin: z.strictObject({ id: z.string(), days: z.int().positive() }).optional(),
      }),
    )
    .min(1),
});

export const EconomySchema = z.strictObject({
  sellGradeMult: z.strictObject({
    common: z.number().min(0),
    magic: z.number().min(0),
    rare: z.number().min(0),
    unique: z.number().min(0),
  }),
  /** Each + level adds this fraction of the base sell price. */
  sellPlusMult: z.number().min(0),
  npcInteractRangeM: z.number().positive(),
  /** Successful upgrades at or above this + are announced to everyone. */
  upgradeBroadcastMinPlus: z.int().min(1),
  /** Most gold a character can hold; also the cap for a stall price and a trade. */
  maxGold: z.int().positive(),
  /** Player stalls (merchant mode). */
  stallMaxItems: z.int().positive(),
  /** Below this level a character cannot set up a stall. */
  stallMinLevel: z.int().min(1),
  /**
   * Locking an item at the inn (owner 2026-10-28): gold a lock costs (unlocking is free); only
   * with Gold Premium, and with the account's four digit PIN.
   */
  itemLockGold: z.int().min(0),
  /**
   * The Genie's time, a balance on the account (owner 2026-10-29): what a new account starts
   * with (the accounts table's default, keep the two alike) and the gift added once a day.
   */
  genie: z.strictObject({
    newAccountMs: z.int().min(0),
    dailyGiftMs: z.int().min(0),
  }),
  stallMaxPrice: z.int().positive(),
  /** Stalls stand at least this far apart (meters): never one on top of another. */
  stallSpacingM: z.number().positive(),
  stallViewRangeM: z.number().positive(),
  /** Player to player trades. */
  tradeMaxItems: z.int().positive(),
  tradeRangeM: z.number().positive(),
  tradeInviteTtlMs: z.int().positive(),
  /** Item slots in each character's warehouse (town warehouse keepers). */
  warehouseSlots: z.int().positive(),
  /**
   * Gear bands by required level: below `mid` low, below `high` mid, the rest high (boss
   * uniques are a band of their own).
   */
  gearBands: z.strictObject({ mid: z.int().positive(), high: z.int().positive() }),
  /**
   * Gold of a common monster by its level: base + perLevel x L + perLevelSq x L^2 on average,
   * its range from minPct to maxPct of that, Umbral Rift monsters riftPct of it. drops.json
   * holds the ranges (checked by gold.test.ts); bosses and event monsters set their own.
   */
  mobGold: z.strictObject({
    base: z.number().min(0),
    perLevel: z.number().min(0),
    perLevelSq: z.number().min(0),
    minPct: z.number().positive(),
    maxPct: z.number().positive(),
    riftPct: z.number().positive(),
  }),
  /**
   * Put in the bag of an account's first character (never again, not after a deletion): the
   * Novice scrolls, each stack within its item's `stack`.
   */
  newAccountGift: z.array(RewardItemSchema),
  /**
   * KO's maps' gift in its place (owner 2026-10-29): the Novice scrolls and a KO wing lent for
   * its `days`.
   */
  koNewAccountGift: z.array(RewardItemSchema),
  /**
   * Inviting friends: when an account that signed up through an invite link gets a character
   * to `level`, both accounts are owed their items (put in the bag at their next entry), for at
   * most `maxRewarded` friends per inviter.
   */
  referral: z.strictObject({
    level: z.int().min(2),
    maxRewarded: z.int().positive(),
    referrer: z.array(z.strictObject({ item: z.string(), qty: z.int().positive() })),
    referred: z.array(z.strictObject({ item: z.string(), qty: z.int().positive() })),
  }),
  /**
   * The daily login reward: one claim per account per day (days turn at midnight in the daily
   * quests' timezone), from the reward window in the game. Each claim takes the next day of the
   * calendar, a missed day included (it waits, never resets); after the last day it starts over.
   * `premiumDays` adds that much Gold Premium to the account, `skin` an armor skin for its days.
   */
  dailyReward: DailyRewardSchema,
  /** KO's maps' calendar in its place, with KO's looks lent for a day (owner 2026-10-29). */
  koDailyReward: DailyRewardSchema,
  /**
   * First week goals: for `days` after a character is made, each goal pays its items once when
   * reached (claimed from the reward window). `level` and `quests` (one-time quests handed in)
   * count up to `target`; `party` (in a party of two or more), `friend` (added a friend) and
   * `upgrade` (tried an upgrade, won or lost) happen once.
   */
  firstWeek: z.strictObject({
    days: z.int().positive(),
    goals: z
      .array(
        z.strictObject({
          id: z.string().regex(/^[a-z0-9_]+$/),
          kind: z.enum(["level", "quests", "party", "friend", "upgrade"]),
          target: z.int().positive(),
          items: z.array(RewardItemSchema).min(1),
        }),
      )
      .min(1),
  }),
});
export type Economy = z.infer<typeof EconomySchema>;
export type FirstWeekGoal = Economy["firstWeek"]["goals"][number];

export const NpcSchema = z.strictObject({
  id: z.string().regex(/^[a-z0-9_]+$/),
  name: z.string(),
  title: z.string(),
  role: z.enum([
    "potions",
    "merchant",
    "scrolls",
    "blacksmith",
    "quartermaster",
    "warehouse",
    "gemcutter",
    "clan",
    "herald",
  ]),
  zone: z.string(),
  x: z.number(),
  z: z.number(),
  /** Degrees, 0 = facing +z. */
  rot: z.number(),
  look: z.strictObject({
    race: RaceSchema,
    gender: GenderSchema,
    outfit: z.enum(["apothecary", "merchant", "scribe", "smith"]),
  }),
  greeting: z.string(),
  /** Who this NPC is, in KO's own words (the newer client's Quest_Npc_Desc), shown under its name. */
  about: z.string().optional(),
  /** Local experiment: drawn as this converted ko-assets look instead of `look`. */
  koLook: z.string().optional(),
  /** Local experiment: drawn as nothing, a click area on a map object (KO's anvil). */
  invisible: z.boolean().optional(),
  services: z.array(
    z.enum([
      "buy",
      "sell",
      "upgrade",
      "warehouse",
      "wheel",
      "reward",
      "generator",
      "smash",
      "lock",
    ]),
  ),
  shop: z.array(
    z.strictObject({
      item: z.string(),
      price: z.int().positive(),
      /** Paid in gold (default) or National Points (the Colony quartermaster). */
      currency: z.enum(["gold", "np"]).optional(),
      /** Only for members of a clan of this grade or better (1 is the best). */
      clanGrade: z.int().min(1).optional(),
      /** Sold only as a pack of this many, `price` the pack's (the Scroll of Speed's 30). */
      pack: z.int().min(2).optional(),
    }),
  ),
});
export const NpcsSchema = z.array(NpcSchema);

const Hex = z.string().regex(/^#[0-9a-f]{6}$/i);

/**
 * Power Up Store: gems are the premium currency (per account). Owner: no gold or power for
 * money, only Gold Premium (faster leveling) and cosmetics.
 */
export const StoreSchema = z.strictObject({
  /** Real money packs (Polar; without it dev credits them for free). */
  gemPacks: z.array(
    z.strictObject({
      id: z.string().regex(/^[a-z0-9_]+$/),
      gems: z.int().positive(),
      bonus: z.int().min(0),
      /** Display price, the Polar product's USD price. */
      price: z.string(),
      /** The same product's TRY price on Polar (checkouts from Turkey), shown to visitors there. */
      priceTry: z.string(),
    }),
  ),
  premium: z.array(
    z.strictObject({
      id: z.string().regex(/^[a-z0-9_]+$/),
      name: z.string(),
      days: z.int().positive(),
      gems: z.int().positive(),
    }),
  ),
  /** Premium: more XP from kills, more gold from loot, better item drop chances, more NP. */
  premiumPerks: z.strictObject({
    xpPct: z.number().min(0),
    goldPct: z.number().min(0),
    /** Each item roll's chance is raised by this percent (a 10% roll becomes 11%). */
    dropPct: z.number().min(0),
    /** NP of a player kill, on top of glows and wings. */
    npPct: z.number().min(0),
    /** Gold of your own (or your party's) kills within this range is picked up by itself. */
    autoLootGoldRangeM: z.number().positive(),
    /** A death to a monster costs no XP (progression.json `deathXpPenalty`). */
    keepXpOnDeath: z.boolean(),
  }),
  /** Armor skins: recolor your plate, mail and cloth; cosmetic only. */
  skins: z.array(
    z.strictObject({
      id: z.string().regex(/^[a-z0-9_]+$/),
      name: z.string(),
      gems: z.int().positive(),
      /** The skin is the account's for this many days; buying it again adds as many. */
      days: z.int().positive(),
      /**
       * A costume item (KO's maps, owner 2026-10-28): the purchase puts it in the bag for `days`
       * and it is worn in the Costume slot like any item, never from the store.
       */
      item: z.string().optional(),
      main: Hex,
      trim: Hex,
      dark: Hex,
      cloth: Hex,
      /** Optional glow on the armor trim. */
      glow: Hex.optional(),
      /** Extra armor pieces the skin adds (horns, crystals, a crown, a cowl, rods). */
      style: z.enum(["horned", "crystal", "regal", "shade", "storm"]),
      /** The moving effect around the wearer. */
      fx: z.enum(["embers", "frost", "halo", "shadow", "storm"]),
      /** One line for the store. */
      desc: z.string(),
    }),
  ),
  /**
   * Hand glow looks (the colors every glow item uses). The store sells each as a Wisp: a
   * glow item without stats that goes to your bag, worn in the Glow slot like any glow.
   */
  /** Items for gems that help upgrades (Fortune Stones), each offer a stack of `qty`. */
  items: z.array(
    z.strictObject({
      id: z.string().regex(/^[a-z0-9_]+$/),
      name: z.string(),
      item: z.string(),
      qty: z.int().min(1),
      gems: z.int().positive(),
      /** A rented item: gone this many days after the purchase (the Market Scroll). */
      days: z.int().positive().optional(),
    }),
  ),
  glows: z.array(
    z.strictObject({
      id: z.string().regex(/^[a-z0-9_]+$/),
      name: z.string(),
      gems: z.int().positive(),
      color: Hex,
      /** The bright center. */
      core: Hex,
      /** Slowly cycles through the colors of the rainbow. */
      cycle: z.boolean().optional(),
      /** The stat-free item the store sells for this look (items.json). */
      item: z.string(),
      /** The Wisp lasts this many days from the purchase, then disappears. */
      days: z.int().positive(),
    }),
  ),
  /**
   * Wings worn on the back: a look (`style` and colors) and the rented item the store sells
   * for it (items.json, slot "wings"), whose stats are small percent bonuses.
   */
  wings: z.array(
    z.strictObject({
      id: z.string().regex(/^[a-z0-9_]+$/),
      name: z.string(),
      gems: z.int().positive(),
      style: z.enum(["feather", "dragon", "fairy"]),
      /** Feathers or membrane. */
      main: Hex,
      /** Tips, bones or veins. */
      edge: Hex,
      /** Optional light along the edges (drawn in the glow layer). */
      glow: Hex.optional(),
      /** Drawn larger (the top offer). */
      grand: z.boolean().optional(),
      item: z.string(),
      days: z.int().positive(),
    }),
  ),
  /**
   * Store cloaks, worn in the Mantle slot: the look drawn on your back in place of the Cloak
   * slot's look (whose stats still count), and the rented item the store sells for it.
   */
  cloaks: z.array(
    z.strictObject({
      id: z.string().regex(/^[a-z0-9_]+$/),
      name: z.string(),
      gems: z.int().positive(),
      /** Extra pieces: ermine and a sun, stars and a crescent, burning runes, leaves and vines. */
      style: z.enum(["royal", "astral", "flame", "leaf"]),
      cloth: Hex,
      /** Edges, clasps and the emblem. */
      trim: Hex,
      /** Fur on the collar and the hem (royal). */
      fur: Hex.optional(),
      /** The lit parts and the motes drifting from the hem. */
      glow: Hex,
      desc: z.string(),
      item: z.string(),
      days: z.int().positive(),
    }),
  ),
});
export type Store = z.infer<typeof StoreSchema>;

/** Game masters: tools and limits. */
export const ModerationSchema = z.strictObject({
  /** GMs move this many times faster than players. */
  gmSpeedMult: z.number().min(1),
  /**
   * How a character played as a GM looks to everyone: their class's armor and weapon of this
   * tier (and shield where the class has one), this cloak, all at this +. A look only: their
   * inventory and stats stay as they are.
   */
  gmLook: z.strictObject({
    tier: z.int().min(0).max(5),
    plus: z.int().min(0),
    cape: z.string(),
  }),
  defaultBanHours: z.number().positive(),
  defaultMuteMinutes: z.number().positive(),
  /** Most mobs one /spawn may create. */
  spawnMax: z.int().positive(),
  /** Flood control on chat (every channel shares one gate per player). */
  chatSpam: z.strictObject({
    /** Least time between two lines in the global chat. */
    globalIntervalMs: z.int().positive(),
    /** Least time between two shouts (heard in the whole zone, every channel). */
    shoutIntervalMs: z.int().positive(),
    /** At most this many lines (any channel) within burstWindowMs. */
    burstMax: z.int().positive(),
    burstWindowMs: z.int().positive(),
    /** The same line again within this time is refused. */
    repeatWindowMs: z.int().positive(),
    /** This many refused lines within strikeWindowMs mutes the player for muteMs. */
    strikesToMute: z.int().positive(),
    strikeWindowMs: z.int().positive(),
    muteMs: z.int().positive(),
  }),
});
export type Moderation = z.infer<typeof ModerationSchema>;

/** Parties and clans. */
/**
 * The Riftstone Reel (the gemcutter in the start town): break a Riftstone and its prizes run
 * past on a reel until it stops. Players see the prizes; how likely each one is lives in
 * wheelOdds.json (server-only). A prize is nothing (`empty`), gold, `qty` of an item, or one
 * item picked at random from `pool` (the unique jewels). `announce` tells the whole server.
 */
export const WheelSegmentSchema = z.strictObject({
  /** Nothing: the stone breaks and is empty. */
  empty: z.literal(true).optional(),
  gold: z.int().positive().optional(),
  item: z.string().optional(),
  qty: z.int().min(1).optional(),
  pool: z.array(z.string()).min(1).optional(),
  /** Shown on the wheel for a pool segment ("Unique jewel"). */
  label: z.string().optional(),
  announce: z.boolean().optional(),
});
export type WheelSegment = z.infer<typeof WheelSegmentSchema>;
/** Riftstones (green, blue, violet) and the Crystal Clash coffers. */
export const WHEEL_TIERS = [
  "green",
  "blue",
  "violet",
  "jade",
  "sapphire",
  "greater_jade",
  "greater_sapphire",
] as const;
export const WheelTierIdSchema = z.enum(WHEEL_TIERS);
export type WheelTierId = z.infer<typeof WheelTierIdSchema>;
export const WheelSchema = z.strictObject({
  tiers: z.record(
    WheelTierIdSchema,
    z.strictObject({
      /** The Riftstone (or coffer) broken for one spin. */
      gem: z.string(),
      segments: z.array(WheelSegmentSchema).min(2),
    }),
  ),
});
export type Wheel = z.infer<typeof WheelSchema>;
/** Server-only: one weight per segment, in segment order. */
export const WheelOddsSchema = z.record(WheelTierIdSchema, z.array(z.number().positive()));
export type WheelOdds = z.infer<typeof WheelOddsSchema>;

/**
 * Quests: taken and handed in from the quest log anywhere (no quest giver). Kill `count` of a
 * mob, collect `count` quest items that drop from it with `chance` while the quest is active,
 * or defeat `count` players of the other nation. Quest items live in the quest log, never in
 * the bag, so they cannot be traded or sold. A quest is done once per character, a `daily`
 * one once per day (reset at midnight in `dailyTimezone`).
 */
export const QuestSchema = z.strictObject({
  id: z.string().regex(/^[a-z0-9_]+$/),
  name: z.string().min(1),
  /** Kills count only in these zones. */
  zones: z.array(z.string()).min(1),
  /** Lowest level that may take it; the reward is sized for this level. */
  level: z.int().min(1),
  text: z.string().min(1),
  daily: z.boolean().optional(),
  /** NPCs that offer it and take it back in their talk window (KO's maps); the log works anywhere. */
  givers: z.array(z.string()).optional(),
  goal: z.discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("kill"), mob: z.string(), count: z.int().min(1) }),
    z.strictObject({
      kind: z.literal("collect"),
      mob: z.string(),
      count: z.int().min(1),
      /** Quest item name shown in the log. */
      item: z.string().min(1),
      chance: z.number().gt(0).max(1),
    }),
    /** Players of the other nation that give National Points (no repeat or low-level kills). */
    z.strictObject({ kind: z.literal("pvp"), count: z.int().min(1) }),
  ]),
  reward: z.strictObject({
    xp: z.int().min(0),
    gold: z.int().min(0),
    /** National Points: spendable only, the NP ranking counts player kills alone. */
    np: z.int().min(0).optional(),
    /** Items put in the bag on hand-in (refused while they do not all fit). */
    items: z.array(z.strictObject({ item: z.string(), qty: z.int().min(1) })).optional(),
  }),
});
export type QuestDef = z.infer<typeof QuestSchema>;
export const QuestsSchema = z.strictObject({
  /** Quests a character may have open at once. */
  maxActive: z.int().min(1),
  /** Daily quests reset at midnight here (IANA name). */
  dailyTimezone: z.string().min(1),
  quests: z.array(QuestSchema),
});
export type Quests = z.infer<typeof QuestsSchema>;

export const SocialSchema = z.strictObject({
  /** Characters of one account in the world at the same time (browser tabs). */
  maxCharactersOnline: z.int().min(1),
  /** Characters of any accounts in the world at once from one device (its browser's id). */
  maxCharactersPerDevice: z.int().min(1),
  /** And from one internet address (a home, a café: several devices). */
  maxCharactersPerIp: z.int().min(1),
  /** Friend lists: players of your nation on the same server whose status you follow. */
  friends: z.strictObject({ max: z.int().min(1) }),
  party: z.strictObject({
    maxMembers: z.int().min(2),
    /** No member more than this many levels from another, unless the two share a clan. */
    maxLevelGap: z.int().min(1),
    /** XP bonus per member beyond the first. */
    xpBonusPerMember: z.number().min(0),
    /** Loot gold bonus per member beyond the first who shares it. */
    goldBonusPerMember: z.number().min(0),
    /** Item drop chance bonus per member beyond the first near the kill. */
    dropBonusPerMember: z.number().min(0),
    /** Members this close to the kill share its XP. */
    xpShareRangeM: z.number().positive(),
    inviteTtlMs: z.int().positive(),
    /** A member who logs out (or changes zone) keeps the place this long. */
    offlineGraceMs: z.int().min(0),
  }),
  clan: z.strictObject({
    maxMembers: z.int().min(2),
    maxOfficers: z.int().min(0),
    createMinLevel: z.int().min(1),
    createCostGold: z.int().min(0),
    nameMin: z.int().min(1),
    nameMax: z.int().min(1),
    inviteTtlMs: z.int().positive(),
    /**
     * Clan grades by the members' lifetime National Points added up (npEarned): a new clan is
     * the last grade and climbs to grade 1. Listed from the lowest grade up.
     */
    grades: z.array(z.strictObject({ grade: z.int().min(1), minNp: z.int().min(0) })).min(1),
  }),
});
export type Social = z.infer<typeof SocialSchema>;

/**
 * Game servers (separate worlds: characters, clans and chat belong to one). Each runs channel 1
 * always and opens the next channel when every open one is busy.
 */
export const ServerSchema = z.strictObject({
  id: z.string().regex(/^[a-z0-9_]+$/),
  name: z.string().min(1),
  maxChannels: z.int().min(1),
  /** Players in one channel (all its zones together) before it counts as full. */
  channelCapacity: z.int().positive(),
  /** The next channel opens once every open channel holds this share of its capacity. */
  openNextAtPct: z.int().min(1).max(100),
  /**
   * Players on the whole server at once (every channel): what its machine runs smoothly. Past
   * it new players wait in a line (Gold Premium first) and enter as others leave.
   */
  maxPlayers: z.int().positive(),
});
export const ServersSchema = z.array(ServerSchema).min(1);
export type ServerDef = z.infer<typeof ServerSchema>;

/** National Points: earned by killing players of the other nation in PvP zones. */
export const PvpSchema = z.strictObject({
  /** NP for a kill: base + victim level * perVictimLevel (rounded down). */
  npBase: z.int().min(0),
  npPerVictimLevel: z.number().min(0),
  /** The victim loses this share of the NP the killer gained (never below 0). */
  npLossFraction: z.number().min(0).max(1),
  /** A victim this many levels (or more) below the killer gives no NP. */
  npLevelGap: z.int().min(1),
  /** Killing the same player again within this time gives no NP (kill farming guard). */
  repeatVictimMs: z.int().min(0),
  /** Symbol next to the name by net NP (earned minus lost); highest matching entry wins. */
  ranks: z
    .array(
      z.strictObject({
        id: z.string().regex(/^[a-z0-9_]+$/),
        name: z.string(),
        minNp: z.int().min(1),
      }),
    )
    .min(1),
  /** Rows per leaderboard page. */
  leaderboardSize: z.int().positive(),
  /**
   * Symbols by place in this month's NP ranking of your nation: the first entry whose `upTo`
   * covers the place. Shown instead of the NP rank symbol.
   */
  placeSymbols: z
    .array(
      z.strictObject({
        id: z.string().regex(/^[a-z0-9_]+$/),
        name: z.string(),
        upTo: z.int().min(1),
      }),
    )
    .min(1),
  /** How often the coordinator works out the places again. */
  placeRefreshMs: z.int().positive(),
  /**
   * Rewards the Herald (an NPC with the `reward` service) gives for a place in a ranking of
   * your nation (`all`: NP earned of all time, `month`: this month's): the item goes to the
   * bag, and is taken back when the place is lost. More can be added here.
   */
  rankRewards: z.array(
    z.strictObject({
      id: z.string().regex(/^[a-z0-9_]+$/),
      item: z.string(),
      ranking: z.enum(["all", "month"]),
      /** This place or better. */
      place: z.int().min(1),
    }),
  ),
  /** The champion's cloak look (the rankRewards item with mantleLook "whitecrown"). */
  championCloak: z.strictObject({
    item: z.string(),
    cloth: Hex,
    trim: Hex,
    glow: Hex,
  }),
});
export type Pvp = z.infer<typeof PvpSchema>;
export type NpcDef = z.infer<typeof NpcSchema>;
